import React from 'react';
import Txt from '../../atoms/Txt/Txt';
import { SURVEY_TIPS, PRIVACY_PROMISE, REFER } from 'components/pages/pages';
import A from 'components/atoms/A/A';
import classes from './Faq.module.scss';
import tagConfigs from 'components/atoms/Tag/tagConfigs';
import TagDescription from 'components/atoms/Tag/TagDescription';

function toHash(heading: string) {
  return heading
    .replace(/ /g, '_')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '');
}
const faqQuestions = [
  {
    key: 'introduction',
    heading: 'About Qmee',
    questions: [
      {
        question: 'Who or what is Qmee?',
        answer: (
          <>
            <Txt component="p">
              Qmee is a free mobile app and desktop site, where you can earn real money for taking
              paid surveys anytime, anywhere. When you complete surveys, Qmee adds your rewards to
              your piggy bank in real time. Plus, with our no minimum cashout amount, you&apos;re
              always in control of your rewards!
            </Txt>
            <Txt component="p">
              Qmee not only has surveys - you can also earn cashback from online purchases, or get
              paid to play with our gaming offers. Plus, add the Qmee app to your desktop or browser
              to earn extra cash when you search for your favorite sites and engage with the hottest
              brands!
            </Txt>
            <Txt component="p">
              there&apos;s even a fun Daily Ponder where you can share your opinion and see what
              other Qmee users think, plus a daily pop quiz - earn cash for knowing the correct
              answer!
            </Txt>
            <Txt component="p">
              Qmee is available in the US, UK, CA, AU, FR and DE, with more countries coming soon!
            </Txt>
          </>
        ),
      },
      {
        question: 'What do I get when using Qmee?',
        answer: (
          <>
            <Txt component="p">
              When you complete surveys and offers with Qmee, you&apos;ll get cash rewards in your
              Qmee piggy bank that you can cashout to PayPal or Venmo at any time - you can even
              choose to treat yourself to a gift card, or donate to one of the charities we work
              with. Plus, if you complete a survey each day, you&apos;ll keep your streak alive and
              get an extra 10% on your survey rewards!
            </Txt>
          </>
        ),
      },
      {
        question: 'How much will I earn?',
        answer: (
          <>
            <Txt component="p">
              There&apos;s no limit to how much you can earn with Qmee and you can start earning
              today! Save your rewards to help buy holiday gifts, or cash out to buy your daily
              coffee.
            </Txt>
            <Txt component="p">
              See how other users have spent their rewards in our{' '}
              <A href="https://www.qmee.com/blog">&apos;Meet Our Qmee Users&apos;</A> series!
            </Txt>
          </>
        ),
      },
      {
        question: 'How do I sign up for Qmee?',
        answer: (
          <>
            <Txt component="p">Signing up for Qmee is free and easy!</Txt>
            <Txt component="p">
              Go to our sign up page and enter your email address and a password of your choice.
              Then, just answer a few simple questions about yourself so you can start being matched
              with surveys that fit your profile and are tailored to you.
            </Txt>
            <Txt component="p">
              It&apos;s super easy to get started - you&apos;ll be topping up your piggy bank in no
              time. Sign up with Qmee to start making money online today!
            </Txt>
            <Txt component="p">
              If you need any help getting started with Qmee, send our helpful support team an email
              at{' '}
              <Txt component="a" href="mailto:support@qmee.com">
                support@qmee.com
              </Txt>
              .
            </Txt>
            <Txt component="p"></Txt>
          </>
        ),
      },
    ],
  },
  {
    key: 'surveys',
    heading: 'Surveys',
    questions: [
      {
        question: 'How do I get surveys?',
        answer: (
          <>
            <Txt component="p">
              Surveys are available to everyone &ndash; you just need to answer a few simple
              questions about yourself to start receiving them. After you&apos;ve signed up to Qmee,
              you&apos;ll see a &apos;Surveys&apos; tab on your Qmee dashboard where you&apos;ll be
              able to tell us a little bit about yourself. Answer these questions honestly, so we
              can provide you with surveys that are right for you!
            </Txt>
          </>
        ),
      },
      {
        question: 'How often will I get surveys?',
        answer: (
          <>
            <Txt component="p">
              New surveys are shown on your &apos;Surveys&apos; tab whenever they become available
              from the survey providers. To ensure you are getting the most surveys possible check
              out our{' '}
              <a
                href="https://www.qmee.com/blog/qmee-survey-checklist"
                target="_blank"
                rel="noreferrer"
              >
                Survey Checklist
              </a>
              !{' '}
            </Txt>
          </>
        ),
      },
      {
        question: 'Why was my survey unsuccessful?',
        answer: (
          <>
            <Txt component="p">
              Unfortunately, some surveys can be deemed unsuccessful and declined by the survey
              provider. There are a number of reasons why this might happen:
            </Txt>
            <ul>
              <Txt component="li">
                <strong>
                  You don&apos;t meet the criteria they&apos;re looking for for that particular
                  survey
                </strong>{' '}
                - the survey providers can be quite picky about who they need answers from, and
                unfortunately, the questions to determine whether you meet this criteria can be
                asked at any point during the survey.
              </Txt>
              <Txt component="li">
                <strong>The maximum number of respondents for that survey has been reached</strong>{' '}
                - all surveys have a quota, and the quota of completed responses can be reached
                whilst you&apos;re still working on your answers.
              </Txt>
              <Txt component="li">
                <strong>
                  The survey providers felt you were answering the questions too quickly,
                  inconsistently or illogically
                </strong>{' '}
                - the providers are always analysing the behaviour of survey takers, to ensure
                they&apos;re collecting high quality responses. They&apos;re not always right, but
                answering surveys carefully helps to reduce these declines.
              </Txt>
            </ul>
            <Txt component="p">
              We know that having a survey declined can be really disheartening and sometimes it
              can&apos;t be avoided. However, there are a few things that you can do to increase
              your successful surveys. Check out our <A href={SURVEY_TIPS.path}>Survey Tips</A> to
              find out how you can become a &apos;Survey Winner&apos;!
            </Txt>
          </>
        ),
      },
      {
        question: 'What do the survey icons mean?',
        answer: (
          <>
            <Txt component="p">
              You may have noticed icons sometimes labelling available surveys on your Survey
              Dashboard. Take a look at the icons below to find out what each of them mean...
            </Txt>

            {Object.entries(tagConfigs.survey)
              .filter(([key, tag]) => tag.detailedDescription)
              .map(([key]) => (
                <TagDescription
                  className={classes.tagDescription}
                  detailed
                  key={key}
                  tag={key}
                  type={'survey'}
                />
              ))}
          </>
        ),
      },
      {
        question: "I've had an issue with a survey, what do I do?",
        answer: (
          <>
            <Txt component="p">
              Please email our support team at <A href="mailto:support@qmee.com">support team</A>{' '}
              with the survey ID and as much detail of the issue as possible. If you don&apos;t know
              the survey ID, please send the date and time you took the survey, and the
              category/name of the survey if you know it. Also, sending any relevant screenshots can
              help the support team diagnose the problem quickly.
            </Txt>
          </>
        ),
      },
      {
        question: 'What is the Daily Ponder?',
        answer: (
          <>
            <Txt component="p">
              Find out what other Qmee users think by answering our daily ponder questions! Each day
              a new question is asked, and you&apos;ll get to see how many people agree with you (or
              don&apos;t agree with you!) when you choose your answer.
            </Txt>
            <Txt component="p">
              You&apos;ll find the Daily Ponder on the homepage &ndash; from asking who you think
              the best superhero is, to finding out your favorite pizza topping, the daily ponder is
              always fun and interesting!
            </Txt>
            <Txt component="p"></Txt>
            <Txt component="p"></Txt>
            <Txt component="p"></Txt>
            <Txt component="p"></Txt>
            <Txt component="p"></Txt>
          </>
        ),
      },
      {
        question: 'What is a survey/daily ponder streak?',
        answer: (
          <>
            <Txt component="p">
              Increase your survey rewards when you maintain a streak of 5 consecutive days! You can
              create and sustain your streak by completing surveys or answering the daily ponder 5
              days in a row. Your reward increase will be available for as long as your streak is
              active and will be shown next to the original survey reward.
            </Txt>
            <Txt component="p">
              On your homepage, you&apos;ll see 5 &apos;Oinqs&apos; which represent how many days
              you have left before you&apos;ve completed your streak - Oinq will be shown as a grey
              outline with a lock inside. Then, when you complete a survey or answer the daily
              ponder each day, the Oinq will turn green.
            </Txt>
            <Txt component="p">
              To help you maintain your streak, you&apos;ll be shown how many hours/minutes you have
              until your next streak task is ready to take, plus how many days you&apos;ve
              maintained your streak - see how long you can keep it going!
            </Txt>
          </>
        ),
      },
    ],
  },
  {
    heading: 'Cashback Offers',
    questions: [
      {
        question: 'What are Cashback Offers?',
        answer: (
          <>
            <Txt component="p">
              We call them our hand-picked money earners! Whether you&apos;re looking to change your
              internet provider, buy a new phone, or even sign up to a free trial, with these
              specially chosen cashback offers you&apos;ll be getting a free cash reward at the same
              time as doing something you were going to do anyway - it&apos;s a win-win situation!
              The first few will appear on your homepage, then just click on &apos;view all&apos;,
              or head to your &apos;cashback&apos; tab, to browse all the options.
            </Txt>
          </>
        ),
      },
      {
        question: 'How do they work?',
        answer: (
          <>
            <Txt component="p">
              First of all, you need to have a Qmee account and be logged in to see which offers are
              currently available. Once you&apos;ve decided which cashback offer you&apos;d like to
              take part in (and which reward you&apos;d like to receive!), you need to click the
              &apos;Go to cashback&apos; button to follow the link to the brand&apos;s website
              &ndash; we&apos;ll track this to ensure you receive your reward if you&apos;re
              eligible and complete the cashback offer. The brand will then let us know when the
              cashback offer has been completed. you&apos;ll receive the reward in your piggybank
              once it has been sent to us by the brand &ndash; this can take up to 90 days, unless
              otherwise stated (for example, if you&apos;ve chosen to take part in a Travel offer,
              the reward may only be given once you&apos;ve completed your trip/stay).
            </Txt>
            <Txt component="p">
              It&apos;s super important to click the &apos;Go to cashback&apos; button when
              you&apos;re trying to get cashback from shopping with a brand. If you search the
              website yourself, or follow a different cashback provider&apos;s link, we cannot track
              the status of your purchase and do not receive the cashback to pass onto you.
            </Txt>
          </>
        ),
      },
      {
        question: 'What do the statuses mean?',
        answer: (
          <>
            <Txt component="p">
              There are a few different statuses that you may see when taking part in a cashback
              offer. Here, you can learn more about what they mean…
            </Txt>
            <Txt component="p">
              <strong>Pending</strong> - the brand has seen that you have made a purchase or
              completed an action on their website, but they still need to check whether you have
              met the criteria of the offer.
            </Txt>
            <Txt component="p">
              <strong>Approved</strong> - it has been confirmed that you completed the offer
              correctly and approved by the brand. The reward has now been added to your piggybank
              and will be shown in your Qmee History - hooray!
            </Txt>
            <Txt component="p">
              <strong>Rejected</strong> - your offer reward has been rejected by the brand. This
              could be for a few different reasons, such as: the order was cancelled, the products
              or services purchased were excluded in the terms of the cashback offer, you were not a
              new customer (some brands only pay cashback to new customers, but this will be written
              in the terms), or it can be something else.{' '}
            </Txt>
          </>
        ),
      },
      {
        question: 'Why is there a wait before my reward is paid?',
        answer: (
          <>
            <Txt component="p">
              You can receive your rewards anywhere between a couple of weeks after completing the
              cashback offer, up to a maximum of 90 days after. There is always a wait before your
              reward is paid, and the length of the wait depends on the specific brand, how quickly
              they review your transaction and confirm you completed the offer, and then when they
              send your reward through to us.
            </Txt>
          </>
        ),
      },
      {
        question: 'Do I have to be a new user to complete the cashback offer?',
        answer: (
          <>
            <Txt component="p">
              You don&apos;t have to be a new Qmee user to complete cashback offers. But, in some
              cases, you must be a new customer to the specific brand you&apos;ve chosen to complete
              an offer with. The terms for each cashback offer are shown at the bottom of the page -
              it will state here whether you need to be a new or existing customer. So, please be
              sure to read through the offer terms prior to completing any offer to avoid
              disappointment!
            </Txt>
          </>
        ),
      },
      {
        question: 'Can I complete a cashback offer more than once?',
        answer: (
          <Txt component="p">
            Unless otherwise stated, an offer can only be completed once and the reward for the
            offer will only be given the first time you complete an offer.
          </Txt>
        ),
      },
      {
        question:
          'Can I receive a reward for a cashback offer I completed previously, outside of Qmee?',
        answer: (
          <>
            <Txt component="p">
              We cannot reward any offer that is completed outside of Qmee. If you have not
              completed an offer by following the &apos;Go to cashback&apos; button on the offer
              pages, we cannot track the status of your purchase and do not receive the cashback to
              pass onto you.
            </Txt>
          </>
        ),
      },
      {
        question: 'Can I complete a cashback offer with Adblock turned on?',
        answer: (
          <>
            <Txt component="p">
              We use cookies to link your offer reward back to your account, and these can be
              blocked by Adblock. To allow offer tracking, make sure you have Adblock turned off
              before clicking on the &apos;Go to cashback&apos; button for the offer you want to
              take part in.
            </Txt>
          </>
        ),
      },
      {
        question: 'Why has my cashback offer reward been rejected?',
        answer: (
          <>
            <Txt component="p">
              If the offer you took part in is showing as &apos;Rejected&apos; in your , this means
              that the brand has not approved you to receive the offer reward. We may not know
              exactly why the brand has reached this decision, but some possible reasons may
              include:
            </Txt>
            <ul>
              <Txt component="li">You canceled or returned all or part of your order.</Txt>
              <Txt component="li">
                You used a promotional code, voucher code or another form of discount not approved
                by the brand when making your purchase.
              </Txt>
              <Txt component="li">
                The brand provides offer rewards to new customers only, and you have shopped with
                this brand before.
              </Txt>
              <Txt component="li">
                You may have used a saved quote or a renewal quote to make this purchase.
              </Txt>
              <Txt component="li">
                You did not complete your order online i.e. it was finalized over the phone.
              </Txt>
              <Txt component="li">You did not stay within the offer terms set by the brand.</Txt>
            </ul>
          </>
        ),
      },
      {
        question: 'How do I cancel or unsubscribe from an offer I completed?',
        answer: (
          <>
            <Txt component="p">
              Products and services subscribed to or purchased through an offer shown on your Qmee
              &apos;Cashback&apos; tab are all from third-party brands. To cancel or unsubscribe
              from these offers, you will need to contact the brand directly. The contact
              information is in the brand&apos;s terms of services or the receipt they provide you
              upon offer completion. If you decide to cancel or unsubscribe from the offer, this may
              affect your receipt of the cash reward.
            </Txt>
          </>
        ),
      },
      {
        question: 'Other important information...',
        answer: (
          <>
            <Txt component="p">
              We cannot be held responsible if the brand fails to report your sale to us. Brands
              have their own rules upon which they determine transactions to be genuine - for
              example, an attempt to simply earn a cash reward by clicking/registering with no
              intention to continue to use/buy from their website could be deemed as non-genuine.
              These rules may or may not be advertised by ourselves or the brand and may be
              discretionary. Rewards will only be credited for offers that are advertised by us and
              that are fully completed.
            </Txt>
          </>
        ),
      },
    ],
  },
  {
    heading: 'General Information',
    questions: [
      {
        question: 'Is Qmee safe? Reassure me...',
        answer: (
          <>
            <Txt component="p">
              The Qmee products and services have been designed with your privacy in mind.
            </Txt>
            <ul>
              <Txt component="li">
                We encrypt all communications and passwords, so that they cannot be read by anyone
                they shouldn&apos;t be.
              </Txt>
              <Txt component="li">
                We don&apos;t share your data with anyone without your permission.{' '}
              </Txt>
              <Txt component="li">
                We don&apos;t store any financial information when you cashout your rewards.{' '}
              </Txt>
              <Txt component="li">
                You can make changes to your details, or request for them to be deleted at any time.
              </Txt>
            </ul>

            <Txt component="p">
              If you want to know more, check out our{' '}
              <A href={PRIVACY_PROMISE.path}>Privacy Promise</A>.
            </Txt>
          </>
        ),
      },
      {
        question: 'What will you do with the information I give you?',
        answer: (
          <>
            <Txt component="p">
              When you join Qmee, you agree to our information use and data protection policy as
              explained in our terms and conditions. In line with this agreement, Qmee does not
              share any data with third parties without your permission.
            </Txt>
            <Txt component="p">
              It&apos;s also a top priority to protect your data when you&apos;re taking surveys.
              So, if you&apos;re declined by a survey, neither the survey provider, nor Qmee keep
              the information you gave in that survey.{' '}
            </Txt>
            <Txt component="p">
              Plus, if you make the decision to leave Qmee, we&apos;ll remove as much information as
              we can about your account, other than data we are required to hold under statute.
            </Txt>
          </>
        ),
      },
      {
        question: 'Where can I use Qmee?',
        answer: (
          <>
            <Txt component="p">
              You can use Qmee, anytime, anywhere! The Qmee app is available on both Android and iOS
              devices, so you can take it wherever you go. Plus, you can access the website version
              of Qmee from your browser, so earning cash from home is super easy.{' '}
            </Txt>
          </>
        ),
      },
      {
        question: 'How can I cashout my Qmee rewards?',
        answer: (
          <>
            <Txt component="p">
              At Qmee, we want to give you the freedom to choose how you spend your hard-earned
              rewards. Here are the current cashout methods that you can use to withdraw your
              earnings:
            </Txt>
            <Txt component="p">
              <strong>PayPal/Venmo</strong>
            </Txt>
            <Txt component="p">
              With no minimum withdrawal amount, you can cashout to your PayPal or Venmo account
              whenever you like. Your rewards will be sent to your account for you to spend however
              you choose!
            </Txt>
            <Txt component="p">
              <strong>Gift cards</strong>
            </Txt>
            <Txt component="p">
              Do you already know exactly how you want to spend your rewards? We&apos;ve got you
              covered! You can choose from our variety of different gift card options at any of the
              set gift card amounts
            </Txt>
            <Txt component="p">
              <strong>Charity</strong>
            </Txt>
            <Txt component="p">
              If you want to use your rewards to help a charity, then you can! When you select the
              Charity option on the cashout page, you can choose to donate your rewards to any of
              the organizations available. We don&apos;t take any commission when you cashout to
              charity, so every penny goes to the charity of your choice.
            </Txt>
            <Txt component="p">
              If you have any other cashout methods or charities that you&apos;d like to see
              available on Qmee, drop us an email at{' '}
              <Txt component="a" href="mailto:support@qmee.com">
                support@qmee.com
              </Txt>
              .
            </Txt>
          </>
        ),
      },
      {
        question: 'How quickly does Qmee pay out?',
        answer: (
          <>
            <Txt component="p">
              There are a variety of ways you can cashout your Qmee rewards, and for all of them, we
              get your cash to you as quickly as possible. When cashing out to PayPal or Venmo with
              no minimum cashout amount, your money will appear in your account within a few
              minutes. If you like to spend your rewards on gift cards, an email with your gift card
              information should be with you just as quickly.
            </Txt>
            <Txt component="p">
              <em>
                Note: Banking hours, public holidays and systems outside Qmee&apos;s control can
                sometimes cause delays.
              </em>
            </Txt>
          </>
        ),
      },
      {
        question: "What if I don't have a PayPal account?",
        answer: (
          <>
            <Txt component="p">
              It&apos;s free and doesn&apos;t take long to sign up for one &ndash; just go to the
              PayPal website. Or, if you don&apos;t want to sign up for a PayPal account, you can
              choose to donate your piggy bank contents to charity.
            </Txt>
          </>
        ),
      },
      {
        question: 'Are there any requirements for linking a PayPal account?',
        answer: (
          <>
            <Txt component="p">
              Any PayPal accounts you try to link to Qmee must meet the following requirements:
            </Txt>
            <ul>
              <Txt component="li">
                The PayPal account must be registered in the same country as your Qmee account.
              </Txt>
              <Txt component="li">
                Your PayPal account must be fully verified. Instructions for how to do this can be
                found{' '}
                <A href={'https://www.qmee.com/blog/how-to-fully-verify-your-paypal-account'}>
                  here
                </A>
                .
              </Txt>
              <Txt component="li">
                The PayPal account must not be linked to another Qmee account.
              </Txt>
            </ul>
          </>
        ),
      },
      {
        question: 'How can I invite my friends?',
        answer: (
          <>
            <Txt component="p">
              Once you&apos;ve been with Qmee for a while, you&apos;ll receive a personal invite
              link that you can share with friends to use when they sign up. To find your invite
              link, head to the <A href={`${REFER.path}`}>Refer a Friend</A> option in your
              &apos;Account&apos; section. You can share your invite link via email or your social
              accounts.
            </Txt>
            <Txt component="p">
              Your referral must sign up through your link and when they make their first cashout to
              PayPal, you&apos;ll earn a reward in your piggy bank. Remind them there is no minimum
              cash out amount!
            </Txt>
          </>
        ),
      },
    ],
  },
  {
    heading: 'My Account',
    questions: [
      {
        question: 'Can I make changes to my account?',
        answer: (
          <>
            <Txt component="p">
              Yes. In your “My Account” section, you&apos;ll find your Survey Profile where you can
              make changes if you need to - we always suggest you keep your profile up to date to
              ensure you receive the right surveys for you. you&apos;ll also find your Login Details
              in your “My Account” section - if you&apos;d like to change the email address or
              password you use to log in to your Qmee account, you can do so here.
            </Txt>
            <Txt component="p">
              <em>
                *Remember, you are only permitted to have one Qmee account, so edit the email
                address on the account you want to use, rather than create a new account.
              </em>
            </Txt>
            <Txt component="p">
              If you are thinking about closing your account, please contact our support team on{' '}
              <Txt component="a" href="mailto:support@qmee.com">
                support@qmee.com
              </Txt>
              .
            </Txt>
          </>
        ),
      },
      {
        question: 'Do I need a different account for each browser, PC or mobile that I use?',
        answer: (
          <>
            <Txt component="p">
              No. You are only permitted to have one Qmee account - you can use the same one across
              all devices and browsers that you use. You just need to log in to your account on
              whichever browser or device you&apos;re using.
            </Txt>
          </>
        ),
      },
      {
        question: 'Can I update my PayPal account details?',
        answer: (
          <>
            <Txt component="p">
              We connect to your PayPal account ID, so you can update your PayPal account details
              via PayPal directly and it won&apos;t impact your Qmee account - you&apos;ll still
              receive your rewards. Just make sure the email you want to receive notifications to is
              set as your primary email in your PayPal account, then when we send you your rewards
              you&apos;ll get notified by PayPal on this email.
            </Txt>
          </>
        ),
      },
      {
        question: 'Can I change the PayPal account linked to my Qmee account?',
        answer: (
          <>
            <Txt component="p">
              In order to make sure everyone receives the right rewards, it&apos;s really important
              we know who people are when they cash out. For this reason, you can&apos;t unlink a
              PayPal account once you&apos;ve linked it. So, please make sure you link to the
              correct PayPal account when you first cash out.
            </Txt>
            <Txt component="p">
              However, you can link additional PayPal accounts if you&apos;d like to change where we
              send your rewards to.
            </Txt>
          </>
        ),
      },
      {
        question: 'Can I change the Venmo account linked to my Qmee account?',
        answer: (
          <>
            <Txt component="p">
              If you&apos;re a US user wanting to change the Venmo account you use to cashout your
              rewards, you can add up to two Venmo accounts. If you want to remove a Venmo account,
              you can request this by emailing{' '}
              <Txt component="a" href="mailto:support@qmee.com">
                support@qmee.com
              </Txt>{' '}
              - but to save you time, try to link the Venmo accounts you will want to cashout from
              in the first instance.
            </Txt>
          </>
        ),
      },
      {
        question: "Why can't I log in?",
        answer: (
          <>
            <Txt component="p">
              If you are having trouble logging in, there are 3 things that could be going wrong.
            </Txt>
            <Txt component="p">
              1 - Are you entering the wrong email address? Double check the welcome email we sent
              you &ndash; it confirms the email address you used.
            </Txt>
            <Txt component="p">
              2 - Have you forgotten your password? No problem, you can reset your password.
            </Txt>
            <Txt component="p">
              3 - Have you tried logging in with the wrong information too many times? If you have,
              you won&apos;t be able to log in for a while. We would suggest waiting 24 hours before
              trying to log in (with the right information) again.
            </Txt>
          </>
        ),
      },
      {
        question: "Why can't I cash out to a gift card?",
        answer: (
          <>
            <Txt component="p">
              You must have a PayPal account linked to your Qmee account before cashing out to gift
              cards - this is because we want to make sure everyone receives the right rewards.
              Before cashing out to a gift card, we need you to verify your identity first by
              cashing out (any amount) to your PayPal account. We don&apos;t want anyone taking your
              hard earned gift cards!
            </Txt>
          </>
        ),
      },
    ],
  },
].map((section) => ({
  ...section,
  key: toHash(section.heading),
  questions: section.questions.map((question) => ({
    ...question,
    key: `${toHash(section.heading)}__${toHash(question.question)}`,
  })),
}));

export default faqQuestions;
